import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { fetchFundamentals as fetchFundamentalsAction } from '../../../actions/market';
import { getDisclosureReportUrl } from '../../../utils/formatter';
import { getAnalysisFundamentals } from '../../../utils/fundamentals';
import useActions from '../../../utils/hooks/useActions';
import useFundamentals from '../../../utils/hooks/useFundamentals';
import useLocalize from '../../../utils/hooks/useLocalize';
import DateTime from '../../common/DateTime';
import Number from '../../common/Number';
import SectionHeaderComponent from '../SectionHeaderComponent';
import { INDICATORS } from './IndicatorsMapping';
import './IndicatorsTabComponent.scss';

const i18nKeyPrefix = 'symbol.details.indicators.fundamentalAnalysis.header';

const getPeriodDate = (fieldData, checkAnnouncementDate, localize) => {
  if (checkAnnouncementDate && !fieldData.announcementDate) return null;
  if (!fieldData.periodDate) return null;
  return (
    <DateTime
      format="YYYY/MM"
      value={fieldData.periodDate}
      suffix={localize(`${i18nKeyPrefix}.period.date`)}
    />
  );
};

const getAnnouncementDate = (fieldData, localize) => {
  if (!fieldData.announcementDate) return null;
  return (
    <DateTime
      format="MM/DD"
      value={fieldData.announcementDate}
      suffix={localize(`${i18nKeyPrefix}.announcement.date`)}
    />
  );
};

const getConsolidated = (fieldData, attributeToCheck, localize) => {
  if (!fieldData[attributeToCheck]) return null;
  const key = fieldData.consolidated ? 'consolidated' : 'nonConsolidated';
  return localize(`${i18nKeyPrefix}.${key}`);
};

const buildIndicatorRow = (
  key,
  indicatorName,
  companyForecast,
  actual,
  analystForecast,
  precision,
  suffix,
  hideForecast,
  isFinalRow
) => {
  return (
    <div key={key} className={`matrix-row${isFinalRow ? ' final-row' : ''}`}>
      <div className="section-name">{indicatorName}</div>
      <div className="actual">
        <Number
          value={actual}
          hideIfZero={true}
          precision={precision}
          suffix={suffix}
        />
      </div>
      <div className="company-forecast">
        <Number
          value={companyForecast}
          hideIfZero={false}
          hideWithoutEmptyIfZero={hideForecast}
          precision={precision}
          suffix={suffix}
        />
      </div>
      <div className="analyst-forecast">
        <Number
          value={analystForecast}
          hideIfZero={!hideForecast}
          hideWithoutEmptyIfZero={hideForecast}
          precision={precision}
          suffix={suffix}
        />
      </div>
    </div>
  );
};

const buildIndicatorMatrix = (analysisFundamentals, localize) => {
  const results = analysisFundamentals.companyResults || {};
  const estimates = analysisFundamentals.companyEstimates || {};
  const analyst = analysisFundamentals.analyst || {};

  return Object.keys(INDICATORS).map((key, index, arr) => {
    const isFinalRow = index === arr.length - 1;
    const suffix =
      INDICATORS[key].suffix === null || INDICATORS[key].suffix === '%'
        ? INDICATORS[key].suffix
        : localize(INDICATORS[key].suffix);
    return buildIndicatorRow(
      key,
      localize(INDICATORS[key].name),
      estimates[key] ?? null,
      results[key] ?? null,
      analyst[key] ?? null,
      INDICATORS[key].precision,
      suffix,
      INDICATORS[key].hideForecast || false,
      isFinalRow
    );
  });
};

const ColumnHeaderComponent = ({ fieldData, labelKey, isAnalyst }) => {
  const localize = useLocalize();

  return (
    <div>
      <div>{getPeriodDate(fieldData, !isAnalyst, localize)}</div>
      <div>
        {localize(`${i18nKeyPrefix}.${labelKey}`)}
        {isAnalyst && localize(`${i18nKeyPrefix}.prediction`)}
      </div>
      {!isAnalyst && fieldData?.announcementDate && (
        <div className="prediction-or-announcement-date">
          ({getAnnouncementDate(fieldData, localize)})
        </div>
      )}
      <div>
        {getConsolidated(
          fieldData,
          isAnalyst ? 'periodDate' : 'announcementDate',
          localize
        )}
      </div>
    </div>
  );
};

const IndicatorsTabComponent = ({ symbol }) => {
  const localize = useLocalize();
  const title = localize('symbol.details.indicators.fundamentalAnalysis.title');
  const [fetchFundamentals] = useActions([fetchFundamentalsAction], []);
  const fundamentals = useFundamentals(symbol);
  const analysisFundamentals = getAnalysisFundamentals(fundamentals) || {};
  const indicatorMatrix = buildIndicatorMatrix(analysisFundamentals, localize);
  const ifisHash = useSelector((state) => state.user?.ifisHash);
  const encryptedId = useSelector((state) => state.user?.encryptedId);
  const disclosureReportUrl = getDisclosureReportUrl(
    symbol,
    ifisHash,
    encryptedId
  );

  useEffect(() => {
    fetchFundamentals(symbol);
  }, [symbol, fetchFundamentals]);

  return (
    <>
      <SectionHeaderComponent
        title={title}
        linkURL={disclosureReportUrl}
        linkMessage={localize(
          'symbol.details.indicators.fundamentalAnalysis.title.financial.results'
        )}
      />
      <div className="indicators-matrix">
        <div className="matrix-row title">
          <div className="section-name">{/* Empty for spacing */}</div>
          <div className="actual">
            <ColumnHeaderComponent
              fieldData={analysisFundamentals.companyResults || {}}
              labelKey="results"
            />
          </div>
          <div className="company-forecast">
            <ColumnHeaderComponent
              fieldData={analysisFundamentals.companyEstimates || {}}
              labelKey="estimates"
            />
          </div>
          <div className="analyst-forecast">
            <ColumnHeaderComponent
              fieldData={analysisFundamentals.analyst || {}}
              labelKey="analyst"
              isAnalyst={true}
            />
          </div>
        </div>
        {indicatorMatrix}
      </div>
    </>
  );
};

export default IndicatorsTabComponent;
